import type { UltraStorageResponseDto } from '@poker-stats/contracts';
import { fetchUltraStorage, saveUltraStorage } from '../api/ultraHudsApi';
import { makeCustomStatId, normalizeUltraPrefs, type UltraPrefs } from './ultraPrefs';

export const ULTRA_HUDS_KEY = 'portableUltraHuds';
const LEGACY_ULTRA_HATS_KEY = 'portableUltraHats';

export const ULTRA_HUD_EXPORT_KIND = 'portable-ultra-hud';
export const ULTRA_HUDS_EXPORT_KIND = 'portable-ultra-huds';
const LEGACY_ULTRA_HAT_EXPORT_KIND = 'portable-ultra-hat';
const LEGACY_ULTRA_HATS_EXPORT_KIND = 'portable-ultra-hats';
export const ULTRA_HUD_EXPORT_VERSION = 1;

export type UltraHud = {
  id: string;
  name: string;
  prefs: UltraPrefs;
  savedAt: string;
};

export type UltraHudExportFile = {
  kind: typeof ULTRA_HUD_EXPORT_KIND;
  version: typeof ULTRA_HUD_EXPORT_VERSION;
  name: string;
  exportedAt: string;
  prefs: UltraPrefs;
};

export type UltraHudsExportFile = {
  kind: typeof ULTRA_HUDS_EXPORT_KIND;
  version: typeof ULTRA_HUD_EXPORT_VERSION;
  exportedAt: string;
  huds: UltraHud[];
};

function makeHudId() {
  return `hud-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeHud(raw: Partial<UltraHud>): UltraHud | null {
  const name = raw.name?.trim();
  if (!name || !raw.prefs) {
    return null;
  }

  return {
    id: raw.id?.trim() || makeHudId(),
    name,
    prefs: normalizeUltraPrefs(raw.prefs),
    savedAt: raw.savedAt ?? new Date().toISOString(),
  };
}

function readStoredHuds(key: string): UltraHud[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored) as Partial<UltraHud>[];
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => normalizeHud(item))
      .filter((item): item is UltraHud => item != null);
  } catch {
    return [];
  }
}

export function readUltraHuds(): UltraHud[] {
  const huds = readStoredHuds(ULTRA_HUDS_KEY);
  if (huds.length > 0) {
    return huds;
  }

  const legacy = readStoredHuds(LEGACY_ULTRA_HATS_KEY);
  if (legacy.length > 0) {
    writeUltraHuds(legacy);
    localStorage.removeItem(LEGACY_ULTRA_HATS_KEY);
  }

  return legacy;
}

export function writeUltraHuds(huds: UltraHud[]) {
  localStorage.setItem(ULTRA_HUDS_KEY, JSON.stringify(huds));
}

export function saveUltraHud(name: string, prefs: UltraPrefs, huds = readUltraHuds()): UltraHud[] {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return huds;
  }

  const normalizedPrefs = normalizeUltraPrefs(prefs);
  const existingIndex = huds.findIndex(
    (hud) => hud.name.toLowerCase() === trimmedName.toLowerCase(),
  );
  const nextHud: UltraHud = {
    id: existingIndex >= 0 ? huds[existingIndex].id : makeHudId(),
    name: trimmedName,
    prefs: normalizedPrefs,
    savedAt: new Date().toISOString(),
  };

  const next =
    existingIndex >= 0
      ? huds.map((hud, index) => (index === existingIndex ? nextHud : hud))
      : [...huds, nextHud];

  writeUltraHuds(next);
  return next;
}

export function deleteUltraHud(id: string, huds = readUltraHuds()): UltraHud[] {
  const next = huds.filter((hud) => hud.id !== id);
  writeUltraHuds(next);
  return next;
}

export function hudExportFilename(name: string) {
  const safe = name
    .trim()
    .replace(/[^\w-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return `ultra-hud-${safe || 'template'}.json`;
}

export function allHudsExportFilename() {
  const stamp = new Date().toISOString().slice(0, 10);
  return `ultra-huds-${stamp}.json`;
}

export function buildUltraHudExport(hud: UltraHud): UltraHudExportFile {
  return {
    kind: ULTRA_HUD_EXPORT_KIND,
    version: ULTRA_HUD_EXPORT_VERSION,
    name: hud.name,
    exportedAt: new Date().toISOString(),
    prefs: normalizeUltraPrefs(hud.prefs),
  };
}

export function buildAllUltraHudsExport(huds: UltraHud[]): UltraHudsExportFile {
  return {
    kind: ULTRA_HUDS_EXPORT_KIND,
    version: ULTRA_HUD_EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    huds: huds.map((hud) => ({
      ...hud,
      prefs: normalizeUltraPrefs(hud.prefs),
    })),
  };
}

export function downloadJsonFile(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function hudFromExportFile(raw: Partial<UltraHudExportFile>): UltraHud | null {
  if (raw.kind !== ULTRA_HUD_EXPORT_KIND && raw.kind !== LEGACY_ULTRA_HAT_EXPORT_KIND) {
    return null;
  }

  if (!raw.prefs) {
    return null;
  }

  return normalizeHud({
    id: makeHudId(),
    name: raw.name ?? 'Imported HUD',
    prefs: raw.prefs,
    savedAt: raw.exportedAt ?? new Date().toISOString(),
  });
}

function parseLegacyHatsExport(raw: Record<string, unknown>): UltraHud[] {
  const hats = raw.hats;
  if (!Array.isArray(hats)) {
    return [];
  }

  return hats
    .map((item) =>
      normalizeHud({
        ...(item as Partial<UltraHud>),
        id: makeHudId(),
        savedAt: (item as Partial<UltraHud>).savedAt ?? new Date().toISOString(),
      }),
    )
    .filter((hud): hud is UltraHud => hud != null);
}

export function parseUltraHudImport(raw: unknown): UltraHud[] {
  if (!raw || typeof raw !== 'object') {
    return [];
  }

  const payload = raw as Record<string, unknown>;
  const kind = payload.kind;

  if (kind === ULTRA_HUDS_EXPORT_KIND && Array.isArray(payload.huds)) {
    return (payload.huds as Partial<UltraHud>[])
      .map((hud) =>
        normalizeHud({
          ...hud,
          id: makeHudId(),
          savedAt: hud.savedAt ?? new Date().toISOString(),
        }),
      )
      .filter((hud): hud is UltraHud => hud != null);
  }

  if (kind === LEGACY_ULTRA_HATS_EXPORT_KIND) {
    return parseLegacyHatsExport(payload);
  }

  if (kind === ULTRA_HUD_EXPORT_KIND || kind === LEGACY_ULTRA_HAT_EXPORT_KIND) {
    const hud = hudFromExportFile(payload as Partial<UltraHudExportFile>);
    return hud ? [hud] : [];
  }

  if ('prefs' in payload && 'name' in payload) {
    const hud = normalizeHud({
      id: makeHudId(),
      name: String(payload.name),
      prefs: payload.prefs as UltraPrefs,
      savedAt: new Date().toISOString(),
    });
    return hud ? [hud] : [];
  }

  return [];
}

function remapCustomStatIds(prefs: UltraPrefs): UltraPrefs {
  const idMap = new Map<string, string>();
  const customStats = prefs.customStats.map((stat) => {
    const nextId = makeCustomStatId();
    idMap.set(stat.id, nextId);
    return { ...stat, id: nextId };
  });

  const statOverrides = { ...prefs.statOverrides };
  for (const [key, value] of Object.entries(prefs.statOverrides)) {
    if (!key.startsWith('custom:')) {
      continue;
    }

    const oldId = key.slice('custom:'.length);
    const nextId = idMap.get(oldId);
    delete statOverrides[key];
    if (nextId) {
      statOverrides[`custom:${nextId}`] = value;
    }
  }

  return {
    ...prefs,
    customStats,
    statOverrides,
  };
}

export function mergeImportedUltraHuds(
  imported: UltraHud[],
  existing = readUltraHuds(),
): UltraHud[] {
  const next = [...existing];

  for (const importedHud of imported) {
    const hud: UltraHud = {
      ...importedHud,
      prefs: remapCustomStatIds(normalizeUltraPrefs(importedHud.prefs)),
      savedAt: new Date().toISOString(),
    };

    const duplicateIndex = next.findIndex(
      (item) => item.name.toLowerCase() === hud.name.toLowerCase(),
    );

    if (duplicateIndex >= 0) {
      next[duplicateIndex] = {
        ...hud,
        id: next[duplicateIndex].id,
      };
    } else {
      next.push({
        ...hud,
        id: makeHudId(),
      });
    }
  }

  writeUltraHuds(next);
  return next;
}

export function applyUltraHud(hud: UltraHud): UltraPrefs {
  return remapCustomStatIds(normalizeUltraPrefs(hud.prefs));
}

function normalizeHudsFromStorage(raw: UltraStorageResponseDto['huds']): UltraHud[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizeHud(item as Partial<UltraHud>))
    .filter((item): item is UltraHud => item != null);
}

export async function pullUltraStorageFromServer(accessToken: string) {
  const storage = await fetchUltraStorage(accessToken);
  const huds = normalizeHudsFromStorage(storage.huds);
  writeUltraHuds(huds);

  return {
    huds,
    activePrefs: storage.activePrefs ? normalizeUltraPrefs(storage.activePrefs) : null,
  };
}

export async function pushUltraStorageToServer(
  accessToken: string,
  huds: UltraHud[],
  activePrefs?: UltraPrefs | null,
) {
  await saveUltraStorage(accessToken, {
    huds: huds.map((hud) => ({
      id: hud.id,
      name: hud.name,
      prefs: normalizeUltraPrefs(hud.prefs),
      savedAt: hud.savedAt,
    })),
    activePrefs: activePrefs ? normalizeUltraPrefs(activePrefs) : null,
  });
}

export async function syncUltraStorageToServer(
  accessToken: string,
  huds: UltraHud[],
  activePrefs?: UltraPrefs | null,
) {
  writeUltraHuds(huds);
  await pushUltraStorageToServer(accessToken, huds, activePrefs ?? null);
}
